import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Eye, Users, MousePointer, TrendingUp, ExternalLink, Smartphone, Monitor, Globe } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { profilePagesAPI } from '../services/api';
import { formatDate } from '../utils/helpers';
import Card from './common/Card';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import Table from './common/Table';

const ProfilePageAnalytics = () => {
  const { id: pageId } = useParams();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [currentPage] = useState(1);
  const [sortBy] = useState('viewed_at');
  const [sortOrder] = useState('DESC');

  const { loading, error, executeApiCall } = useApi();

  const fetchAnalytics = useCallback(async () => {
    try {
      console.log('Fetching analytics for pageId:', pageId);
      const params = {
        page: currentPage,
        limit: 50,
        sortBy,
        sortOrder
      };
      
      // Try direct API call first
      const token = localStorage.getItem('token');
      console.log('Token available:', !!token);
      
      const result = await executeApiCall(profilePagesAPI.getAnalytics, pageId, params);
      console.log('Analytics result:', result);
      
      if (result.success) {
        setAnalyticsData(result.data);
      } else {
        console.error('API call failed:', result.error);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  }, [pageId, currentPage, sortBy, sortOrder, executeApiCall]);

  useEffect(() => {
    console.log('useEffect triggered with pageId:', pageId);
    if (pageId) {
      fetchAnalytics();
    } else {
      console.log('No pageId available');
    }
  }, [pageId, currentPage, sortBy, sortOrder, fetchAnalytics]);

  const getSourceIcon = (source) => {
    switch (source) {
      case 'facebook': return <ExternalLink className="h-4 w-4 text-blue-600" />;
      case 'twitter': return <ExternalLink className="h-4 w-4 text-blue-400" />;
      case 'instagram': return <ExternalLink className="h-4 w-4 text-pink-600" />;
      case 'linkedin': return <ExternalLink className="h-4 w-4 text-blue-700" />;
      case 'youtube': return <ExternalLink className="h-4 w-4 text-red-600" />;
      case 'tiktok': return <ExternalLink className="h-4 w-4 text-black" />;
      case 'whatsapp': return <ExternalLink className="h-4 w-4 text-green-600" />;
      case 'telegram': return <ExternalLink className="h-4 w-4 text-blue-500" />;
      case 'email': return <ExternalLink className="h-4 w-4 text-purple-600" />;
      case 'search': return <ExternalLink className="h-4 w-4 text-yellow-600" />;
      case 'direct': return <Globe className="h-4 w-4 text-ink-600" />;
      default: return <ExternalLink className="h-4 w-4 text-ink-600" />;
    }
  };

  const getSourceBadgeColor = (source) => {
    switch (source) {
      case 'facebook': return 'bg-blue-100 text-blue-800';
      case 'twitter': return 'bg-blue-100 text-blue-800';
      case 'instagram': return 'bg-pink-100 text-pink-800';
      case 'linkedin': return 'bg-blue-100 text-blue-800';
      case 'youtube': return 'bg-red-100 text-red-800';
      case 'tiktok': return 'bg-ink-100 text-ink-800';
      case 'whatsapp': return 'bg-green-100 text-green-800';
      case 'telegram': return 'bg-blue-100 text-blue-800';
      case 'email': return 'bg-purple-100 text-purple-800';
      case 'search': return 'bg-yellow-100 text-yellow-800';
      case 'direct': return 'bg-ink-100 text-ink-800';
      default: return 'bg-ink-100 text-ink-800';
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'android':
        return <Smartphone className="h-4 w-4 text-green-600" />;
      case 'ios':
        return <Smartphone className="h-4 w-4 text-blue-600" />;
      case 'desktop':
        return <Monitor className="h-4 w-4 text-purple-600" />;
      default:
        return <Globe className="h-4 w-4 text-ink-600" />;
    }
  };

  // Debug info
  console.log('ProfilePageAnalytics render - pageId:', pageId, 'loading:', loading, 'error:', error, 'analyticsData:', analyticsData);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner text="Loading analytics..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        title="Error"
        message="Failed to load analytics. Please try again."
      />
    );
  }

    if (!analyticsData) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-center">
          <p className="text-ink-500 text-lg">No analytics data available</p>
          <p className="text-ink-400 text-sm">Page ID: {pageId}</p>
        </div>
      </div>
    );
  }

  const { stats, linkPerformance, trafficSources, analytics, clickAnalytics, clickStats } = analyticsData;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
            Profile page analytics
          </h1>
          <p className="mt-1 text-sm text-ink-500 font-mono">Page #{pageId}</p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="self-start sm:self-auto inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 transition shadow-soft"
        >
          Refresh
        </button>
      </header>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { icon: Eye, label: 'Total views', value: stats?.total_views || 0, accent: 'from-brand-500 to-violet-600' },
          { icon: Users, label: 'Unique visitors', value: stats?.unique_visitors || 0, accent: 'from-emerald-500 to-teal-600' },
          { icon: MousePointer, label: 'Total clicks', value: clickStats?.total_clicks || 0, accent: 'from-pink-500 to-rose-500' },
          { icon: TrendingUp, label: 'Unique clickers', value: clickStats?.unique_clickers || 0, accent: 'from-amber-500 to-orange-500' },
        ].map(({ icon: Icon, label, value, accent }) => (
          <div key={label} className="rounded-2xl border border-ink-200 bg-white p-5 shadow-soft">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-ink-500">{label}</p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-ink-900">{(value || 0).toLocaleString()}</p>
              </div>
              <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-soft`}>
                <Icon className="h-4 w-4" />
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Link Performance */}
      {linkPerformance && linkPerformance.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-ink-900 mb-4">Link Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Link
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Total Clicks
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Unique Clicks
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    From This Page
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    CTR
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-100">
                {linkPerformance.map((link, index) => {
                  const ctr = stats?.total_views ? ((link.clicks_from_this_page / stats.total_views) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={link.link_id || index} className="hover:bg-ink-50">
                      <td className="px-4 py-4">
                        <div className="max-w-xs truncate">
                          <p className="text-sm font-medium text-ink-900">{link.title || link.original_url}</p>
                          <p className="text-xs text-ink-500">{link.short_code}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-ink-900">
                        {link.total_clicks || 0}
                      </td>
                      <td className="px-4 py-4 text-sm text-ink-900">
                        {link.unique_clicks || 0}
                      </td>
                      <td className="px-4 py-4 text-sm text-ink-900">
                        {link.clicks_from_this_page || 0}
                      </td>
                      <td className="px-4 py-4 text-sm text-ink-900">
                        {ctr}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Click Analytics */}
      {clickAnalytics && clickAnalytics.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ink-900">Click Analytics</h3>
            <div className="text-sm text-ink-500">
              {clickStats?.total_clicks || 0} total clicks from {clickStats?.unique_clickers || 0} unique visitors
            </div>
          </div>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>
                  Date & Time
                </Table.Header>
                <Table.Header>
                  Link
                </Table.Header>
                <Table.Header>
                  Platform
                </Table.Header>
                <Table.Header>
                  Device
                </Table.Header>
                <Table.Header>
                  IP Address
                </Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {clickAnalytics.map((click, index) => (
                <Table.Row key={click.id || index}>
                  <Table.Cell className="whitespace-nowrap text-sm text-ink-900">
                    {formatDate(click.clicked_at)}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="max-w-xs truncate">
                      <p className="text-sm font-medium text-ink-900">{click.link_title || click.original_url}</p>
                      <p className="text-xs text-ink-500">{click.short_code}</p>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getPlatformIcon(click.platform)}
                      <span className="text-sm text-ink-900 capitalize">
                        {click.platform || 'Unknown'}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-sm text-ink-900 capitalize">
                    {click.device_type || 'Unknown'}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-sm text-ink-500">
                    {click.ip_hash ? click.ip_hash.slice(0, 10) : 'Unknown'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Card>
      )}

      {/* Traffic Sources */}
      {trafficSources && trafficSources.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-ink-900 mb-4">Traffic Sources</h3>
          <div className="space-y-3">
            {trafficSources.map((source, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-ink-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {getSourceIcon(source.source)}
                  <span className="text-sm font-medium text-ink-900 capitalize">
                    {source.source === 'direct' ? 'Direct Traffic' : source.source}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-ink-900">{source.views} views</p>
                  <p className="text-xs text-ink-500">{source.unique_visitors} unique</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Page Views Table */}
      {analytics && analytics.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-ink-900 mb-4">Recent Page Views</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                    Visitor
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-100">
                {analytics.map((view, index) => (
                  <tr key={view.id || index} className="hover:bg-ink-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-ink-900">
                      {formatDate(view.viewed_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getPlatformIcon(view.platform)}
                        <span className="text-sm text-ink-900 capitalize">
                          {view.platform || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-ink-900 capitalize">
                      {view.device_type || 'Unknown'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceBadgeColor(view.source)}`}>
                        {view.source === 'direct' ? 'Direct' : view.source}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-ink-500">
                      {view.ip_hash ? view.ip_hash.slice(0, 10) : 'Unknown'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ProfilePageAnalytics;
